import React from "react";
import {
  Button,
  Col,
  Collapse,
  Form,
  Input,
  Modal,
  Row,
} from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  EyeOutlined,
  PlusOutlined,
  UpOutlined,
} from "@ant-design/icons";

import RangePickerComp from "../../../../components/common/RangePickerComp";
import SwitchComp from "../../../../components/common/SwitchComp";
import UploadFormItem from "../../Features/components/UploadFormItem";
import {
  CODE_TYPES,
  ENTRY_TYPES,
  HAND_TITLED_TYPES,
  SLIDER_STYLE_TYPES,
  URL_PLACEHOLDER,
} from "./blockItems";
import { PREVIEW_SANDBOX, previewSrc } from "./blockPreview";

const { Panel } = Collapse;

const AddBannerControl = ({ value, onChange, onAdd }) => (
  <>
    <div className="font-weight-bold mb-2">Add banner</div>
    <Form.Item label="Title" required style={{ marginBottom: 8 }}>
      <div className="d-flex align-items-center" style={{ gap: 8 }}>
        <Input
          style={{ flex: 1 }}
          placeholder="Type in"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onPressEnter={(event) => {
            event.preventDefault();
            if (value.trim()) onAdd();
          }}
        />
        <Button
          type="primary"
          shape="circle"
          icon={<PlusOutlined />}
          disabled={!value.trim()}
          onClick={onAdd}
        />
      </div>
    </Form.Item>
  </>
);

const BannerCard = ({
  name,
  index,
  active,
  open,
  onToggleActive,
  onToggleOpen,
  onRemove,
  children,
}) => (
  <div
    className="mb-3"
    style={{
      border: "1px solid #EFEFEF",
      borderRadius: 4,
      opacity: active ? 1 : 0.6,
    }}
  >
    <div
      className="d-flex align-items-center justify-content-between px-2 py-1"
      style={{ background: "#F7F7F7" }}
    >
      <div className="d-flex align-items-center" style={{ gap: 8 }}>
        <SwitchComp className="" checked={active} onChange={onToggleActive} />
        <span className="font-weight-bold" style={{ fontSize: 13 }}>
          {name || `Banner ${index + 1}`}
        </span>
      </div>
      <div className="d-flex align-items-center" style={{ gap: 8 }}>
        {open ? (
          <UpOutlined
            style={{ color: "#454545", cursor: "pointer" }}
            onClick={onToggleOpen}
          />
        ) : (
          <DownOutlined
            style={{ color: "#454545", cursor: "pointer" }}
            onClick={onToggleOpen}
          />
        )}
        <DeleteOutlined
          style={{ color: "#C94747", cursor: "pointer" }}
          onClick={onRemove}
        />
      </div>
    </div>

    <div className="px-2 pt-2" style={{ display: open ? "block" : "none" }}>
      {children}
    </div>
  </div>
);

const PanelHeader = ({ label, active, onToggleActive }) => (
  <div className="d-flex align-items-center" style={{ gap: 8 }}>
    <div onClick={(event) => event.stopPropagation()} role="presentation">
      <SwitchComp className="" checked={active} onChange={onToggleActive} />
    </div>
    <span className="text-black">{label}</span>
  </div>
);

const BlockBanners = ({
  blockType,
  typeLabel,
  banners,
  fieldPrefixFor,
  onAdd,
  onRemove,
  onUpdate,
  form,
}) => {
  const [openKeys, setOpenKeys] = React.useState([]);
  const [bannerTitle, setBannerTitle] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const seenKeys = React.useRef(new Set());

  React.useEffect(() => {
    const fresh = banners
      .map((banner) => banner.key)
      .filter((key) => !seenKeys.current.has(key));

    if (!fresh.length) return;

    fresh.forEach((key) => seenKeys.current.add(key));
    setOpenKeys((prev) => [...prev, ...fresh]);
  }, [banners]);

  const toggleOpen = (key) => {
    setOpenKeys((prev) =>
      prev.includes(key)
        ? prev.filter((openKey) => openKey !== key)
        : [...prev, key]
    );
  };

  const isText = blockType === "text";
  const contentLabel = isText ? "Visible text" : "HTML code";
  const addEntryLabel = isText
    ? "Add text field"
    : blockType === "html"
      ? "Add HTML field"
      : "Add title";

  const contentRules = (entry) => {
    if (!entry.active) return [];
    if (!isText) return [{ required: true, message: "Can't be empty" }];

    const hiddenName = `${fieldPrefixFor(entry)}_hidden_content`;
    return [
      ({ getFieldValue }) => ({
        validator: (_rule, value) =>
          value || getFieldValue(hiddenName)
            ? Promise.resolve()
            : Promise.reject(
              new Error("Fill in the visible or the hidden text")
            ),
      }),
    ];
  };

  const contentField = ({
    entry,
    field,
    stateKey = field,
    label,
    extra,
    placeholder,
    rules,
    dependencies,
  }) => {
    const name = `${fieldPrefixFor(entry)}_${field}`;

    return (
      <div className="d-flex align-items-start" style={{ gap: 8 }}>
        <Form.Item
          className="flex-grow-1"
          label={label}
          extra={extra}
          name={name}
          rules={rules}
          dependencies={dependencies}
        >
          <Input.TextArea
            rows={5}
            placeholder={placeholder}
            onChange={(event) =>
              onUpdate(entry.key, { [stateKey]: event.target.value })
            }
          />
        </Form.Item>
        <Form.Item label=" ">
          <Button
            shape="circle"
            icon={<EyeOutlined style={{ color: "#FFA940" }} />}
            onClick={() => setPreview(form.getFieldValue(name) || "")}
          />
        </Form.Item>
      </div>
    );
  };

  const previewModal = (
    <Modal
      title="Preview"
      visible={preview !== null}
      footer={null}
      width={720}
      onCancel={() => setPreview(null)}
    >
      {preview ? (
        isText ? (
          <div style={{ whiteSpace: "pre-wrap" }}>{preview}</div>
        ) : (
          <iframe
            title="HTML preview"
            src={previewSrc(preview)}
            sandbox={PREVIEW_SANDBOX}
            style={{
              width: "100%",
              height: 360,
              border: "1px solid #EFEFEF",
              borderRadius: 4,
            }}
          />
        )
      ) : (
        <div className="text-muted">Nothing to preview yet.</div>
      )}
    </Modal>
  );

  if (ENTRY_TYPES.includes(blockType)) {
    return (
      <>
        <Collapse
          activeKey={openKeys}
          onChange={(keys) => setOpenKeys(Array.isArray(keys) ? keys : [keys])}
          expandIconPosition="right"
        >
          {banners.map((entry, index) => (
            <Panel
              key={entry.key}
              forceRender
              header={
                <PanelHeader
                  label={`${typeLabel} #${index + 1}`}
                  active={entry.active}
                  onToggleActive={() =>
                    onUpdate(entry.key, { active: !entry.active })
                  }
                />
              }
              extra={
                banners.length > 1 && (
                  <DeleteOutlined
                    style={{ color: "#C94747" }}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemove(entry.key);
                    }}
                  />
                )
              }
            >
              <Row gutter={16} align="bottom">
                <Col span={16}>
                  <Form.Item
                    label="Title"
                    name={`${fieldPrefixFor(entry)}_title`}
                  >
                    <Input
                      placeholder="Type in"
                      onChange={(event) =>
                        onUpdate(entry.key, { title: event.target.value })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label=" ">
                    <SwitchComp
                      className=""
                      checked={entry.showTitle}
                      onChange={() =>
                        onUpdate(entry.key, { showTitle: !entry.showTitle })
                      }
                      text={entry.showTitle ? "Show" : "Off"}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {contentField({
                entry,
                field: "content",
                label: contentLabel,
                extra: isText ? "Always shown on the page." : undefined,
                placeholder: isText ? "Type in" : "Paste HTML code",
                rules: contentRules(entry),
                dependencies: isText
                  ? [`${fieldPrefixFor(entry)}_hidden_content`]
                  : undefined,
              })}

              {isText &&
                contentField({
                  entry,
                  field: "hidden_content",
                  stateKey: "hiddenContent",
                  label: "Hidden text",
                  extra:
                    'Shown below the visible text after the visitor clicks "Show more". Leave empty to show no toggle.',
                  placeholder: "Type in",
                  rules: [],
                })}
            </Panel>
          ))}
        </Collapse>

        <Button
          className="w-100 mt-2 mb-3"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => onAdd()}
        >
          {addEntryLabel}
        </Button>

        {previewModal}
      </>
    );
  }

  if (blockType === "hero_banner") {
    return (
      <>
        <Collapse
          activeKey={openKeys}
          onChange={(keys) => setOpenKeys(Array.isArray(keys) ? keys : [keys])}
          expandIconPosition="right"
        >
          {banners.map((banner, index) => (
            <Panel
              key={banner.key}
              forceRender
              header={
                <PanelHeader
                  label={`Banner ${index + 1}`}
                  active={banner.active}
                  onToggleActive={() =>
                    onUpdate(banner.key, { active: !banner.active })
                  }
                />
              }
              extra={
                banners.length > 1 && (
                  <DeleteOutlined
                    style={{ color: "#C94747" }}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemove(banner.key);
                    }}
                  />
                )
              }
            >
              <Row gutter={16}>
                <Col span={12}>
                  <UploadFormItem
                    name={`${fieldPrefixFor(banner)}_desktop`}
                    label="Desktop image"
                    requiredMessage="Please upload an image!"
                    greyText="Recommended .jpeg, .png format"
                    required={banner.active}
                    onFileChange={(file, url) =>
                      onUpdate(banner.key, {
                        desktopImageFile: file,
                        desktopImageUrl: url,
                      })
                    }
                    externalURL={banner.desktopImageUrl}
                  />
                </Col>
                <Col span={12}>
                  <UploadFormItem
                    name={`${fieldPrefixFor(banner)}_mobile`}
                    label="Mobile image"
                    greyText="Optional, falls back to the desktop image"
                    required={false}
                    onFileChange={(file, url) =>
                      onUpdate(banner.key, {
                        mobileImageFile: file,
                        mobileImageUrl: url,
                      })
                    }
                    externalURL={banner.mobileImageUrl}
                  />
                </Col>
              </Row>

              <Form.Item
                label="Destination URL"
                name={`${fieldPrefixFor(banner)}_url`}
              >
                <Input
                  placeholder={URL_PLACEHOLDER}
                  onChange={(event) =>
                    onUpdate(banner.key, {
                      destinationUrl: event.target.value,
                    })
                  }
                />
              </Form.Item>
            </Panel>
          ))}
        </Collapse>

        <Button
          className="w-100 mt-2 mb-3"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => onAdd()}
        >
          Add banner
        </Button>
      </>
    );
  }

  if (!HAND_TITLED_TYPES.includes(blockType)) return null;

  return (
    <>
      <AddBannerControl
        value={bannerTitle}
        onChange={setBannerTitle}
        onAdd={() => {
          onAdd({ name: bannerTitle.trim() });
          setBannerTitle("");
        }}
      />

      {banners.map((banner, index) => (
        <BannerCard
          key={banner.key}
          name={banner.name}
          index={index}
          active={banner.active}
          open={openKeys.includes(banner.key)}
          onToggleActive={() => onUpdate(banner.key, { active: !banner.active })}
          onToggleOpen={() => toggleOpen(banner.key)}
          onRemove={() => onRemove(banner.key)}
        >
          {SLIDER_STYLE_TYPES.includes(blockType) && (
            <Row gutter={16} align="bottom">
              <Col span={16}>
                <Form.Item
                  label="Banner title"
                  name={`${fieldPrefixFor(banner)}_title`}
                >
                  <Input
                    placeholder="Type in"
                    onChange={(event) =>
                      onUpdate(banner.key, { title: event.target.value })
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label=" ">
                  <SwitchComp
                    className=""
                    checked={banner.showTitle}
                    onChange={() =>
                      onUpdate(banner.key, { showTitle: !banner.showTitle })
                    }
                    text="Show"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {blockType === "carousel" && (
            <Form.Item
              label="Validity period"
              name={`${fieldPrefixFor(banner)}_validity`}
            >
              <RangePickerComp
                showTime={{ format: "HH:mm" }}
                placeholder={["Start time", "End time"]}
                disabledDate={() => false}
              />
            </Form.Item>
          )}

          {SLIDER_STYLE_TYPES.includes(blockType) ? (
            <UploadFormItem
              name={`${fieldPrefixFor(banner)}_desktop`}
              label="Image #1"
              requiredMessage="Please upload an image!"
              greyText="Recommended .jpeg, .png format"
              required={banner.active}
              onFileChange={(file, url) =>
                onUpdate(banner.key, {
                  desktopImageFile: file,
                  desktopImageUrl: url,
                })
              }
              externalURL={banner.desktopImageUrl}
            />
          ) : (
            <Row gutter={16}>
              <Col span={12}>
                <UploadFormItem
                  name={`${fieldPrefixFor(banner)}_desktop`}
                  label="Desktop image"
                  requiredMessage="Please upload an image!"
                  greyText="Recommended .jpeg, .png format, width 1200 px, flexible height"
                  required={banner.active}
                  onFileChange={(file, url) =>
                    onUpdate(banner.key, {
                      desktopImageFile: file,
                      desktopImageUrl: url,
                    })
                  }
                  externalURL={banner.desktopImageUrl}
                />
              </Col>
              <Col span={12}>
                <UploadFormItem
                  name={`${fieldPrefixFor(banner)}_mobile`}
                  label="Mobile image"
                  greyText="Optional, falls back to the desktop image"
                  required={false}
                  onFileChange={(file, url) =>
                    onUpdate(banner.key, {
                      mobileImageFile: file,
                      mobileImageUrl: url,
                    })
                  }
                  externalURL={banner.mobileImageUrl}
                />
              </Col>
            </Row>
          )}

          <Form.Item
            label="Destination URL"
            name={`${fieldPrefixFor(banner)}_url`}
          >
            <Input
              placeholder={URL_PLACEHOLDER}
              onChange={(event) =>
                onUpdate(banner.key, { destinationUrl: event.target.value })
              }
            />
          </Form.Item>
        </BannerCard>
      ))}
    </>
  );
};

export default BlockBanners;
